import React, { useState } from "react";
import styled from "styled-components";
import { Button, Input, message, Modal, Space, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { DeleteOutlined, EditOutlined, QrcodeOutlined } from "@ant-design/icons";
import QuantityControl from "../../../../../components/shared-components/QuantityControl";
import dayjs from "dayjs";
import { LOG_ACTION_SOLD } from "../../../../../constants/CratesConstants";
import { omitBundlePackaging, updateMultipleProductCountedStock } from "../../../../../functions/productStock";

const { Text } = Typography;

const sanitizeCrateDocId = (crateId) => {
  if (!crateId) return crateId;
  return String(crateId).replaceAll("/", "_").trim();
};

const ProductDetailModal = ({
  selectedProduct,
  visible,
  scannedBundleItems,
  setScannedBundleItems,
  fulfillmentData,
  setFulfillmentData,
  order,
  originalShoppingCart,
  pickingCrate,
  setPickingCrate,
  awaitingCrateScan,
  setAwaitingCrateScan,
  pickingScannedCount,
  setPickingScannedCount,
  pickingBaseFulfilled,
  setPickingBaseFulfilled,
  emptiedCrates,
  setEmptiedCrates,
  pickedCrates,
  setPickedCrates,
  isSaving,
  setIsSaving,
  tempFulfilled,
  setTempFulfilled,
  setProductModalVisible,
  setScanBundleModalVisible,
  handleRemoveProduct,
  handleUnpick,
  getCrateStockItem,
  applySoldToCrate,
  appendCratePickEvent,
  handleUpdateFulfillment,
  restockCratesForUnpick,
  handlePickingCrateScan,
  getSelectedProductBarcode,
  getPersistedCrateLinesForProduct,
  findCrateStockByCrateId,
  crateStocks,
  products,
}) => {
  const { t } = useTranslation();
  const [manualCrateModalVisible, setManualCrateModalVisible] = useState(false);
  const [manualCrateId, setManualCrateId] = useState("");
  const [manualCrateSubmitting, setManualCrateSubmitting] = useState(false);

  return (
    <>
      <Modal
        title={<h1>{selectedProduct?.productName}</h1>}
        open={visible}
        onCancel={() => {
          if (scannedBundleItems && !fulfillmentData[selectedProduct?.productId]?.bundleData) {
            Modal.confirm({
              title: t("bundle_scanning.unsaved_data_warning_title"),
              content: t("bundle_scanning.unsaved_data_warning_message"),
              okText: t("yes"),
              cancelText: t("cancel"),
              onOk: () => {
                setProductModalVisible(false);
                setPickingCrate(null);
                setPickingScannedCount(0);
                setScannedBundleItems(null);
              },
            });
          } else {
            setProductModalVisible(false);
            setPickingCrate(null);
            setPickingScannedCount(0);
            setScannedBundleItems(null);
          }
        }}
        footer={[
          <div key="footer">
            <div style={{ marginBottom: 20, textAlign: "center" }}>
              {selectedProduct?.isBundle && (
                <Button
                  key="scan-bundle"
                  type="default"
                  icon={<QrcodeOutlined />}
                  onClick={() => {
                    setScanBundleModalVisible(true);
                  }}
                  disabled={order.packed || pickingScannedCount > 0}
                  size={"large"}
                >
                  {t("bundle_scanning.scan_underlying_products")}
                </Button>
              )}
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <div>
                {selectedProduct &&
                  !originalShoppingCart.some((item) => item.productId === selectedProduct.productId) && (
                    <Button
                      key="remove"
                      danger
                      size={"large"}
                      onClick={() => handleRemoveProduct(selectedProduct?.productId)}
                    >
                      <DeleteOutlined /> {t("remove")}
                    </Button>
                  )}
                <Button
                  key="unpick"
                  onClick={() => handleUnpick(selectedProduct?.productId)}
                  disabled={order.packed || isSaving}
                  loading={isSaving}
                  size={"large"}
                  style={{ marginLeft: "10px" }}
                >
                  {t("unpick")}
                </Button>
              </div>
              <div>
                <Button
                  key="save"
                  type="primary"
                  loading={isSaving}
                  onClick={async () => {
                    if (!selectedProduct) return;
                    if (tempFulfilled === null || tempFulfilled === undefined) return;
                    if (tempFulfilled < 0) return;

                    const productBarcode = getSelectedProductBarcode();

                    if (pickingCrate && !awaitingCrateScan) {
                      const crateQty = Number(getCrateStockItem(pickingCrate, productBarcode)?.quantity || 0);
                      const absoluteMax = Number(pickingBaseFulfilled || 0) + crateQty;
                      if (Number(tempFulfilled || 0) > absoluteMax) {
                        message.error(t("order_picking.quantity_exceeds_items_in_crate"));
                        return;
                      }
                    }

                    try {
                      setIsSaving(true);

                      const productId = selectedProduct.productId;
                      const prevFulfilled = Number(
                        fulfillmentData?.[productId]?.fulfilled ?? selectedProduct?.fulfilled ?? 0,
                      );
                      const nextFulfilled = Number(tempFulfilled || 0);

                      if (scannedBundleItems && selectedProduct?.isBundle) {
                        const bundleProductId = productId;
                        const completedBundles = Number(scannedBundleItems.completedBundles || 0);

                        // Track quantities used per underlying product for countedStock adjustment
                        // Use scannedItems which contains ALL scanned quantities (with and without crates)
                        const underlyingProductQuantities = omitBundlePackaging(
                          scannedBundleItems.scannedItems,
                          bundleProductId,
                          [selectedProduct],
                          products,
                        );

                        for (const c of scannedBundleItems.pickedCrates || []) {
                          const qty = Number(c?.pickedQty || 0);
                          if (!c?.crateDocId || qty <= 0) continue;

                          const crate =
                            findCrateStockByCrateId(c.crateId) ||
                            (crateStocks || []).find((x) => x?.id === c.crateDocId) ||
                            null;
                          if (!crate) continue;

                          const soldMeta = await applySoldToCrate({
                            crate,
                            soldQty: qty,
                            productIdOrBarcode: c.productId,
                          });
                          const crateItem = getCrateStockItem(crate, c.productId);
                          await appendCratePickEvent({
                            type: LOG_ACTION_SOLD,
                            productId: c.productId,
                            bundleProductId,
                            productBarcode: soldMeta?.productBarcode || c?.productBarcode || null,
                            crateDocId: soldMeta?.crateDocId || c.crateDocId,
                            crateId: soldMeta?.crateId || c.crateId,
                            quantity: qty,
                            remainingQuantity: qty,
                            packageDateUnix: crateItem?.packageDateUnix || null,
                          });
                        }

                        if (
                          scannedBundleItems.currentPickingCrate &&
                          Number(scannedBundleItems.currentPickingScannedCount || 0) > 0 &&
                          scannedBundleItems.currentActiveProductId
                        ) {
                          const qty = Number(scannedBundleItems.currentPickingScannedCount || 0);
                          const soldMeta = await applySoldToCrate({
                            crate: scannedBundleItems.currentPickingCrate,
                            soldQty: qty,
                            productIdOrBarcode: scannedBundleItems.currentActiveProductId,
                          });
                          const crateItem = getCrateStockItem(
                            scannedBundleItems.currentPickingCrate,
                            scannedBundleItems.currentActiveProductId,
                          );
                          await appendCratePickEvent({
                            type: LOG_ACTION_SOLD,
                            productId: scannedBundleItems.currentActiveProductId,
                            bundleProductId,
                            productBarcode: soldMeta?.productBarcode || null,
                            crateDocId: soldMeta?.crateDocId || scannedBundleItems.currentPickingCrate?.id,
                            crateId: soldMeta?.crateId || scannedBundleItems.currentPickingCrate?.crateId,
                            quantity: qty,
                            remainingQuantity: qty,
                            packageDateUnix: crateItem?.packageDateUnix || null,
                          });
                        }

                        // Update countedStock: decrease for underlying products, increase for bundle
                        try {
                          const stockUpdates = [];

                          // Decrease countedStock for each underlying product
                          for (const [underlyingProductId, usedQty] of Object.entries(underlyingProductQuantities)) {
                            stockUpdates.push({
                              productId: underlyingProductId,
                              quantityDelta: -usedQty,
                            });
                          }

                          // Increase countedStock for the bundle product
                          if (completedBundles > 0) {
                            stockUpdates.push({
                              productId: bundleProductId,
                              quantityDelta: completedBundles,
                            });
                          }

                          // Batch update all countedStock changes
                          if (stockUpdates.length > 0) {
                            await updateMultipleProductCountedStock(stockUpdates, false);
                          }
                        } catch (error) {
                          console.error("Error updating countedStock for bundle assembly:", error);
                          message.warning(t("order_picking.counted_stock_update_failed"));
                        }
                      }

                      for (const c of pickedCrates || []) {
                        const qty = Number(c?.pickedQty || 0);
                        if (!c?.crateDocId || qty <= 0) continue;

                        const crate =
                          findCrateStockByCrateId(c.crateId) ||
                          (crateStocks || []).find((x) => x?.id === c.crateDocId) ||
                          null;
                        if (!crate) continue;

                        const soldMeta = await applySoldToCrate({ crate, soldQty: qty, productIdOrBarcode: productId });
                        const crateItem = getCrateStockItem(crate, productId);
                        await appendCratePickEvent({
                          type: LOG_ACTION_SOLD,
                          productId,
                          productBarcode:
                            soldMeta?.productBarcode || c?.productBarcode || selectedProduct.productBarcode || null,
                          crateDocId: soldMeta?.crateDocId || c.crateDocId,
                          crateId: soldMeta?.crateId || c.crateId,
                          quantity: qty,
                          remainingQuantity: qty,
                          packageDateUnix: crateItem?.packageDateUnix || null,
                        });
                      }

                      if (pickingCrate && !awaitingCrateScan) {
                        const delta = Number(tempFulfilled || 0) - Number(pickingBaseFulfilled || 0);
                        if (delta > 0) {
                          const soldMeta = await applySoldToCrate({
                            crate: pickingCrate,
                            soldQty: delta,
                            productIdOrBarcode: productId,
                          });
                          const pickingCrateItem = getCrateStockItem(pickingCrate, productId);
                          await appendCratePickEvent({
                            type: LOG_ACTION_SOLD,
                            productId,
                            productBarcode: soldMeta?.productBarcode || selectedProduct.productBarcode || null,
                            crateDocId: soldMeta?.crateDocId || pickingCrate?.id,
                            crateId: soldMeta?.crateId || pickingCrate?.crateId,
                            quantity: delta,
                            remainingQuantity: delta,
                            packageDateUnix: pickingCrateItem?.packageDateUnix || null,
                          });
                        }
                      }

                      if (nextFulfilled < prevFulfilled) {
                        const toRestock = prevFulfilled - nextFulfilled;
                        await restockCratesForUnpick({
                          productId,
                          productBarcode,
                          quantity: toRestock,
                        });
                      }
                    } catch (e) {
                      console.error("Failed to save crate picking", e);
                      message.error(t("error_saving_progress"));
                      return;
                    } finally {
                      setIsSaving(false);
                    }

                    await handleUpdateFulfillment(selectedProduct?.productId, tempFulfilled, true);

                    if (scannedBundleItems) {
                      setFulfillmentData((prev) => ({
                        ...prev,
                        [selectedProduct.productId]: {
                          ...prev[selectedProduct.productId],
                          bundleData: scannedBundleItems,
                        },
                      }));
                    }

                    setProductModalVisible(false);
                    setPickingCrate(null);
                    setPickingScannedCount(0);
                    setScannedBundleItems(null);
                  }}
                  size={"large"}
                >
                  {t("save")}
                </Button>
              </div>
            </div>
          </div>,
        ]}
      >
        {selectedProduct && (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <div style={{ width: "100%", marginBottom: 16 }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {getPersistedCrateLinesForProduct(selectedProduct.productId).length > 0 && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    {getPersistedCrateLinesForProduct(selectedProduct.productId).map((line) => (
                      <div
                        key={`persisted-crate-line-${line.crateId}`}
                        style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
                      >
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {t("order_picking.crate_line_item", { crateId: line.crateId, quantity: line.quantity })}
                        </Text>
                      </div>
                    ))}
                  </div>
                )}

                {(emptiedCrates || []).map((c) => (
                  <div
                    key={`emptied-${c.crateDocId || c.crateId}`}
                    style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
                  >
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {t("order_picking.crate_empty_scanned_line", {
                        crateId: c.crateId || c.crateDocId,
                        scannedCount: Number(c.scannedCount || 0),
                      })}
                    </Text>
                  </div>
                ))}

                {scannedBundleItems && Number(scannedBundleItems.completedBundles || 0) > 0 && (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {t("order_picking.bundle_parts_fulfilled", {
                        completedBundles: scannedBundleItems.completedBundles,
                      })}
                    </Text>
                  </div>
                )}

                {!scannedBundleItems &&
                  fulfillmentData?.[selectedProduct?.productId]?.bundleData &&
                  Number(fulfillmentData[selectedProduct.productId].bundleData.completedBundles || 0) > 0 && (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {t("order_picking.bundle_parts_fulfilled", {
                          completedBundles: fulfillmentData[selectedProduct.productId].bundleData.completedBundles,
                        })}
                      </Text>
                    </div>
                  )}

                {!pickingCrate ? (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Text style={{ fontSize: 12 }}>{t("order_picking.scan_crate")}</Text>
                    <Space size={4}>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {t("order_picking.waiting_for_crate_scan")}
                      </Text>
                      <Button
                        type="text"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => {
                          setManualCrateId("");
                          setManualCrateModalVisible(true);
                        }}
                      />
                    </Space>
                  </div>
                ) : (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Text style={{ fontSize: 12 }}>
                      {t("order_picking.current_crate", { crateId: pickingCrate?.crateId || pickingCrate?.id })}
                    </Text>
                    <Space size={6}>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {t("order_picking.scanned_count", { count: pickingScannedCount })}
                      </Text>
                      <Button
                        size="small"
                        onClick={async () => {
                          if (!pickingCrate) return;

                          const crateDocId = pickingCrate.id || sanitizeCrateDocId(pickingCrate.crateId);
                          const crateId = pickingCrate.crateId || pickingCrate.id;
                          const item = getCrateStockItem(pickingCrate, getSelectedProductBarcode());
                          const pickedQty = Number(pickingScannedCount || 0);

                          if (pickedQty > 0) {
                            setPickedCrates((prev) => {
                              const next = Array.isArray(prev) ? [...prev] : [];
                              const exists = next.some(
                                (c) => String(c?.crateDocId || "").trim() === String(crateDocId || "").trim(),
                              );
                              if (!exists) {
                                next.push({
                                  crateId: String(crateId || "").trim(),
                                  crateDocId: String(crateDocId || "").trim(),
                                  pickedQty,
                                  productBarcode: item?.barcode || selectedProduct?.productBarcode || null,
                                  status: "emptied",
                                  createdAtUnix: dayjs().unix(),
                                });
                              }
                              return next;
                            });
                          }

                          setEmptiedCrates((prev) => {
                            const next = Array.isArray(prev) ? [...prev] : [];
                            const exists = next.some(
                              (c) => String(c?.crateDocId || "").trim() === String(crateDocId || "").trim(),
                            );
                            if (!exists) {
                              next.push({
                                crateId: String(crateId || "").trim(),
                                crateDocId: String(crateDocId || "").trim(),
                                scannedCount: pickedQty,
                                createdAtUnix: dayjs().unix(),
                              });
                            }
                            return next;
                          });

                          setPickingBaseFulfilled(Number(tempFulfilled || 0));
                          setAwaitingCrateScan(true);
                          setPickingCrate(null);
                          setPickingScannedCount(0);
                        }}
                      >
                        {t("order_picking.empty")}
                      </Button>
                    </Space>
                  </div>
                )}
              </div>
            </div>
            <QuantityDisplayContainer>
              <QuantityColumn>
                <QuantityLabel>{t("requested")}</QuantityLabel>
                <QuantityValue fontSize="56px" color="#4A5568">
                  {selectedProduct.requested}
                </QuantityValue>
              </QuantityColumn>
              <QuantityColumn>
                <QuantityLabel>{t("fulfilled")}</QuantityLabel>
                <QuantityValue fontSize="64px" color="#9B2C6B">
                  {tempFulfilled}
                </QuantityValue>
              </QuantityColumn>
            </QuantityDisplayContainer>

            <div style={{ marginBottom: "40px" }}>
              <QuantityControl
                isMobile={true}
                isPickingFlow={true}
                productId={selectedProduct.productId}
                currentQuantity={tempFulfilled}
                onIncrease={() => {
                  if (!pickingCrate || awaitingCrateScan) {
                    setTempFulfilled((prev) => Number(prev || 0) + 1);
                    return;
                  }
                  const base = Number(pickingBaseFulfilled || 0);
                  const scanned = Number(pickingScannedCount || 0);
                  const next = Number(tempFulfilled || 0) + 1;
                  const crateQty = Number(getCrateStockItem(pickingCrate, getSelectedProductBarcode())?.quantity || 0);
                  const absoluteMax = base + crateQty;
                  if (next > absoluteMax) {
                    message.error(t("order_picking.quantity_exceeds_items_in_crate"));
                    return;
                  }
                  const maxAllowed = base + scanned;
                  if (next > maxAllowed) {
                    setPickingScannedCount((prev) => Number(prev || 0) + (next - maxAllowed));
                  }
                  setTempFulfilled(next);
                }}
                onDecrease={() => {
                  setTempFulfilled((prev) => Math.max(0, prev - 1));
                }}
                onManualInput={(productId, value) => {
                  const base = Number(pickingBaseFulfilled || 0);
                  const scanned = Number(pickingScannedCount || 0);
                  const next = Math.max(0, Number(value || 0));

                  if (!pickingCrate || awaitingCrateScan) {
                    setTempFulfilled(next);
                    return;
                  }

                  if (pickingCrate) {
                    const crateQty = Number(
                      getCrateStockItem(pickingCrate, getSelectedProductBarcode())?.quantity || 0,
                    );
                    const absoluteMax = base + crateQty;
                    if (next > absoluteMax) {
                      message.error(t("order_picking.quantity_exceeds_items_in_crate"));
                      return;
                    }
                  }

                  const maxAllowed = base + scanned;
                  if (next > maxAllowed) {
                    setPickingScannedCount((prev) => Number(prev || 0) + (next - maxAllowed));
                  }
                  setTempFulfilled(next);
                }}
              />
            </div>
          </div>
        )}
      </Modal>

      <Modal
        title={t("order_picking.manual_crate_entry_title")}
        open={manualCrateModalVisible}
        onCancel={() => {
          setManualCrateModalVisible(false);
          setManualCrateId("");
        }}
        okText={t("ok")}
        cancelText={t("cancel")}
        confirmLoading={manualCrateSubmitting}
        onOk={async () => {
          const code = String(manualCrateId || "").trim();
          if (!code) return;

          setManualCrateSubmitting(true);
          try {
            const success = await handlePickingCrateScan(code);
            if (success) {
              setManualCrateModalVisible(false);
              setManualCrateId("");
            }
          } finally {
            setManualCrateSubmitting(false);
          }
        }}
      >
        <Input
          value={manualCrateId}
          onChange={(e) => setManualCrateId(e.target.value)}
          placeholder={t("order_picking.manual_crate_entry_placeholder")}
          inputMode="numeric"
          autoFocus
          onPressEnter={async () => {
            const code = String(manualCrateId || "").trim();
            if (!code) return;

            setManualCrateSubmitting(true);
            try {
              const success = await handlePickingCrateScan(code);
              if (success) {
                setManualCrateModalVisible(false);
                setManualCrateId("");
              }
            } finally {
              setManualCrateSubmitting(false);
            }
          }}
        />
      </Modal>
    </>
  );
};

const QuantityDisplayContainer = styled.div`
  display: flex;
  justify-content: space-around;
  margin-top: 20px;
  margin-bottom: 20px;
  gap: 30px;
`;

const QuantityColumn = styled.div`
  text-align: center;
`;

const QuantityLabel = styled(Text)`
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  color: #666;
  display: block;
`;

const QuantityValue = styled(Text)`
  font-size: ${(props) => props.fontSize || "56px"};
  font-weight: bold;
  color: ${(props) => props.color || "#4A5568"};
  line-height: 64px;
`;

export default ProductDetailModal;
